import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Avatar, ChecklistField, Truncated, useControlledField } from '@frontend/design-system';
import { radioCardStyles } from '../styles';
import { ProviderFields } from './provider-fields';

interface Props {
  providerList?: { id: string; firstName: string; lastName: string; src?: string }[];
  selectedProviderIds?: string[];
  setSelectedProviders: (ids: string[]) => void;
  firstName?: string;
  lastName?: string;
  setFirstName: (name: string) => void;
  setLastName: (name: string) => void;
}

const ADD_PROVIDER_OPTION = 'add-provider';

export const ProviderSelectionStep = ({
  providerList,
  selectedProviderIds,
  setSelectedProviders,
  firstName,
  lastName,
  setFirstName,
  setLastName,
}: Props) => {
  const { t } = useTranslation('schedule-onboarding');
  const providerCheckListField = useControlledField({
    type: 'checklist',
    value: selectedProviderIds || [],
    onChange: (value) => setSelectedProviders(value),
  });

  if (!providerList?.length) {
    return (
      <section css={{ display: 'flex', gap: theme.spacing(2), '> div': { flex: 1 } }}>
        <ProviderFields
          firstName={firstName}
          lastName={lastName}
          setFirstName={setFirstName}
          setLastName={setLastName}
        />
      </section>
    );
  }

  const isOtherSelected = providerCheckListField.value.includes(ADD_PROVIDER_OPTION);

  return (
    <ChecklistField
      {...providerCheckListField}
      name='provider-checklist'
      label=''
      orientation='horizontal'
      css={[checklistFieldStyle, radioCardStyles.field]}
    >
      {providerList?.map((provider) => (
        <ChecklistField.Option key={provider.id} name={provider.id} css={checklistFieldOptionStyle}>
          <Avatar size='small' firstName={provider.firstName} lastName={provider.lastName} src={provider.src} />
          <Truncated css={optionTextStyle}>
            {provider.firstName} {provider.lastName}
          </Truncated>
        </ChecklistField.Option>
      ))}
      <motion.div
        layout
        initial={false}
        transition={{ layout: { duration: 0.3, ease: 'easeOut' } }}
        css={[{ overflow: 'hidden', '> div': checklistFieldOptionStyle }, isOtherSelected && expandedOption]}
      >
        <ChecklistField.Option
          name={ADD_PROVIDER_OPTION}
          css={{ '> label': { flexDirection: 'column', alignItems: 'flex-start' } }}
        >
          <div css={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Avatar size='small' iconName='plus' css={plusAvatarStyle} />
            <Truncated css={optionTextStyle}>{t('Add Provider')}</Truncated>
          </div>
          {isOtherSelected && (
            <section css={inputWrapperStyle}>
              <ProviderFields
                firstName={firstName}
                lastName={lastName}
                setFirstName={setFirstName}
                setLastName={setLastName}
              />
            </section>
          )}
        </ChecklistField.Option>
      </motion.div>
    </ChecklistField>
  );
};

const inputWrapperStyle = css({
  width: '100%',
  padding: theme.spacing(1, 1, 0, 1),
  display: 'flex',
  gap: theme.spacing(2),
  '> *': { flex: 1 },
});

const optionTextStyle = css({
  marginLeft: theme.spacing(1),
  fontWeight: theme.font.weight.bold,
});

const checklistFieldStyle = css({
  'label:empty': { display: 'none' },
});
const checklistFieldOptionStyle = css({
  minHeight: 72,
  alignItems: 'center',
  backgroundColor: theme.colors.white,
  borderRadius: theme.borderRadius.medium,
  border: `1px solid ${theme.colors.neutral40}`,
  paddingLeft: theme.spacing(2),
  margin: 0,
  color: theme.colors.primary50,
  transition: 'all 250ms ease-out',

  ':hover': {
    backgroundColor: theme.colors.neutral5,
    '> div::before': { transform: 'scale(1)' },
  },
  ':focus-within, :focus': {
    borderColor: theme.colors.primary50,
    outlineColor: theme.colors.primary50,
    outlineOffset: '4px',
  },
  '> label': {
    overflow: 'hidden',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    padding: theme.spacing(1, 1, 1, 0),
  },
});

const expandedOption = css({
  gridColumn: '1 / -1', // Span full width across all columns
  alignItems: 'flex-start',
  transition: 'all 0.3s ease-out',
  '> label': {
    width: '100%',
  },
});

const plusAvatarStyle = css({
  svg: { color: theme.colors.primary50 },
  div: { backgroundColor: theme.colors.primary10 },
});
